import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import HeroContent from './HeroContent';
import { WhatWeDo, AgenticAI, AppsStrip, PosterCTA } from './HomeSections';
import KuestionPage from './pp-kuestion/KuestionPage';
import TerminalXPage from './pp-terminalx/TerminalXPage';
import DownloadsPage from './downloads/DownloadsPage';
import Footer from './Footer';
import PageLoader from './PageLoader';
import useScroll3D from '../../hooks/useScroll3D';
import useTheme from '../../hooks/useTheme';
import useAppTheme from '../../hooks/useAppTheme';
import { DOWNLOADS_PATH } from '../../config/downloads';
import { TERMINALX_PATH } from '../../config/terminalx';
import './MasterPage.css';

/**
 * Four screens behind one navbar:
 *   home       Riftworks only
 *   kuestion   the Kuestion application page, swapped in by the Applications
 *              menu (same showAltMenu contract the old Backupman page used)
 *   terminalx  the TerminalX application page, at its own address, in the
 *              app's own colour theme (useAppTheme) while it is open
 *   downloads  the Downloads page, which has its own address so the app and
 *              anything else can link straight to it
 *
 * TerminalX and Downloads are decided by the address; home / Kuestion by state.
 */
export default function MasterPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const onDownloads = location.pathname === DOWNLOADS_PATH;
  const onTerminalx = location.pathname === TERMINALX_PATH;
  const offHome = onDownloads || onTerminalx;
  // Arriving from an addressed page with "open the Kuestion page" carried in the navigation.
  const [showAltMenu, setShowAltMenu] = useState(() => !offHome && location.state?.page === 'kuestion');
  const [siteTheme, toggleSiteTheme] = useTheme();
  // While TerminalX is open the shell wears the app's palette and the toggle
  // switches that; the site's own choice is untouched and returns on leaving.
  const [theme, toggleAppTheme] = useAppTheme(onTerminalx ? 'terminalx' : null, siteTheme);

  // The design's deep links: …/#downloads and …/#terminalx open those pages.
  useEffect(() => {
    if (location.hash === '#downloads' && !onDownloads) navigate(DOWNLOADS_PATH, { replace: true });
    if (location.hash === '#terminalx' && !onTerminalx) navigate(TERMINALX_PATH, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openKuestion = () => {
    setShowAltMenu(true);
    if (offHome) navigate('/', { state: { page: 'kuestion' } });
  };
  const goHome = () => {
    setShowAltMenu(false);
    if (offHome) navigate('/');
  };
  const goDownloads = () => {
    setShowAltMenu(false);
    if (!onDownloads) navigate(DOWNLOADS_PATH);
  };
  const goTerminalx = () => {
    setShowAltMenu(false);
    if (!onTerminalx) navigate(TERMINALX_PATH);
  };

  useEffect(() => { window.scrollTo(0, 0); }, [showAltMenu, onDownloads, onTerminalx]);
  useScroll3D([showAltMenu, onDownloads, onTerminalx]);

  return (
    <div className="rw-page">
      <PageLoader />
      <Navbar
        showAltMenu={showAltMenu && !offHome}
        setShowAltMenu={(v) => (v ? openKuestion() : goHome())}
        theme={theme}
        onToggleTheme={onTerminalx ? toggleAppTheme : toggleSiteTheme}
        onDownloads={goDownloads}
        downloadsActive={onDownloads}
        onTerminalx={goTerminalx}
      />

      {onDownloads ? (
        <DownloadsPage />
      ) : onTerminalx ? (
        <TerminalXPage />
      ) : showAltMenu ? (
        <KuestionPage />
      ) : (
        <main>
          <HeroContent onSeeApps={openKuestion} />
          <WhatWeDo />
          <AgenticAI />
          <AppsStrip onOpen={openKuestion} onOpenTerminalx={goTerminalx} />
          <PosterCTA
            title="Tell us the task. We'll automate it."
            body="IT, education or practice work — if it repeats, it can run itself."
            action="Talk to us"
            href="#contact"
          />
        </main>
      )}

      <Footer />
    </div>
  );
}
